import { BadgePercent, Handshake, Receipt, SearchCheck, UsersRound, Workflow } from "lucide-react";
import type { PageLocale } from "@/utils/pageLocale";

const adoptionSteps = [
  {
    title: "Evaluate",
    description:
      "Identify useful AI opportunities, choose the right starting point, and connect adoption priorities to real business work.",
    icon: SearchCheck,
  },
  {
    title: "Enable",
    description:
      "Give leaders and teams practical training so people understand the tools, use them responsibly, and build confidence together.",
    icon: UsersRound,
  },
  {
    title: "Adopt",
    description:
      "Turn promising use cases into repeatable workflows with deployment guidance and ongoing implementation support.",
    icon: Workflow,
  },
];

const pricingBenefits = [
  {
    title: "Discounted partner pricing",
    description:
      "As an OpenAI SMB Channel Partner, we can offer eligible small and medium-sized businesses discounted pricing on OpenAI business plans, below what you would pay buying direct.",
    icon: BadgePercent,
  },
  {
    title: "Licenses and adoption from one partner",
    description:
      "Purchase your seats through Tech Leaders and get the training, workflow design, and implementation support bundled with them, instead of juggling a vendor and a consultant.",
    icon: Handshake,
  },
  {
    title: "Simple procurement",
    description:
      "One conversation, one quote, one invoice. We handle the partner paperwork so your team can get started without a drawn-out buying process.",
    icon: Receipt,
  },
];

const pricingSteps = [
  "Tell us how many people you want on AI tools and what they need to do with them.",
  "We confirm your eligibility and send a partner-pricing quote for the right OpenAI plan.",
  "Your seats are provisioned and your adoption plan starts the same week.",
];

const supportPoints = [
  "Discounted partner pricing on OpenAI business plans",
  "Leadership alignment around the right AI use cases",
  "Hands-on training tailored to how your team works",
  "Workflow design and deployment guidance",
  "Ongoing implementation and adoption support",
  "Responsible use with sensible governance",
];

const en = {
  adoptionSteps, pricingBenefits, pricingSteps, supportPoints,
  "seoTitle": "OpenAI SMB Channel Partner | Technical Leaders",
  "seoDescription": "Tech Leaders is an OpenAI SMB Channel Partner offering small and medium-sized businesses discounted partner pricing on OpenAI business plans, plus the training, workflow design, and implementation support to adopt them.",
  "announcement": "Announced July 28, 2026",
  "headline": "A practical path from AI curiosity to everyday adoption",
  "intro": "Tech Leaders is participating as an OpenAI SMB Channel Partner, helping small and medium-sized businesses evaluate, deploy, and adopt AI with the training, workflow clarity, and hands-on support their teams need.",
  "pricingPill": "Partner pricing: pay less for OpenAI plans through Tech Leaders",
  "heroCta": "Get Partner Pricing",
  "pressCta": "Read the PR Newswire Release",
  "proof": "8,000+ professionals trained across 2,000+ organizations",
  "pricingLabel": "Partner pricing",
  "pricingHeadline": "Buy OpenAI through Tech Leaders and pay less than direct",
  "pricingIntro": "Channel partners can offer OpenAI business plans at discounted partner pricing. That means your team gets the same OpenAI tools, at a lower price, with an adoption partner already in place on day one.",
  "pricingHow": "How partner pricing works",
  "renewal": "Already paying OpenAI directly? Ask us about moving your existing seats to partner pricing at your next renewal.",
  "quoteCta": "Request a Partner Pricing Quote",
  "adoptionLabel": "What this means for your team",
  "adoptionHeadline": "Support across the adoption journey",
  "adoptionIntro": "The technology is only one part of successful AI adoption. Teams also need a clear starting point, practical skills, redesigned workflows, and support that carries promising experiments into everyday work.",
  "supportLabel": "From interest to implementation",
  "supportHeadline": "Practical help, built around how your organization works",
  "supportIntro": "Through the program, Tech Leaders has a clearer path to help SMB clients discover, purchase, deploy, and adopt OpenAI offerings as part of a practical, well-supported adoption plan.",
  "bottomHeadline": "Talk through the right next step for your team",
  "bottomIntro": "Schedule a free 15-minute phone conversation to identify a useful starting point, surface the biggest adoption blocker, and get a partner-pricing quote for the OpenAI plan that fits your team.",
  "bottomCta": "Schedule an AI Strategy Call",
  "disclaimer": "OpenAI and the OpenAI logo are trademarks of OpenAI. Tech Leaders participates as an OpenAI SMB Channel Partner. Partner pricing is available on eligible OpenAI business plans for qualifying small and medium-sized businesses and is confirmed in your quote.",
  "keywords": [
    "OpenAI SMB Channel Partner",
    "OpenAI partner",
    "OpenAI partner pricing",
    "discounted ChatGPT for business",
    "AI adoption",
    "AI training for teams",
    "AI workflow design",
    "small business AI"
  ]
};

const pt: typeof en = {
  "seoTitle": "Parceiro OpenAI para empresas | Technical Leaders",
  "seoDescription": "A Tech Leaders participa do programa OpenAI SMB Channel Partner e oferece condições comerciais diferenciadas em planos empresariais elegíveis, capacitação prática e apoio à adoção de IA para pequenas e médias empresas.",
  "announcement": "Anunciado em 28 de julho de 2026",
  "headline": "Leve a IA para o dia a dia da sua empresa",
  "intro": "A Tech Leaders participa do programa OpenAI SMB Channel Partner e ajuda pequenas e médias empresas a avaliar, implementar e adotar IA, com capacitação prática e apoio para transformar o trabalho das equipes.",
  "pricingPill": "Planos OpenAI com condições de parceiro pela Tech Leaders",
  "heroCta": "Solicitar uma cotação",
  "pressCta": "Leia o comunicado na PR Newswire (em inglês)",
  "proof": "Mais de 8.000 profissionais capacitados em mais de 2.000 organizações",
  "pricingLabel": "Condições comerciais de parceiro",
  "pricingHeadline": "Contrate planos OpenAI pela Tech Leaders e pague menos do que na contratação direta",
  "pricingIntro": "Parceiros de canal podem oferecer planos empresariais da OpenAI com condições comerciais diferenciadas. Sua equipe tem acesso às mesmas ferramentas, com um preço menor e apoio à adoção desde o primeiro dia.",
  "pricingHow": "Como funciona a contratação pela parceria",
  "renewal": "Sua empresa já contrata a OpenAI diretamente? Converse conosco sobre a possibilidade de migrar suas licenças para as condições de parceiro na próxima renovação.",
  "quoteCta": "Solicitar uma cotação de parceiro",
  "adoptionLabel": "O que isso significa para sua equipe",
  "adoptionHeadline": "Apoio em cada etapa da adoção de IA",
  "adoptionIntro": "A tecnologia é apenas uma parte da adoção de IA. As equipes também precisam de um ponto de partida claro, habilidades práticas, processos de trabalho redesenhados e apoio para levar boas experiências ao dia a dia.",
  "supportLabel": "Do interesse à implementação",
  "supportHeadline": "Apoio prático, pensado para a realidade da sua empresa",
  "supportIntro": "Por meio do programa, a Tech Leaders ajuda pequenas e médias empresas a conhecer, contratar, implementar e adotar soluções da OpenAI, com um plano prático e acompanhamento em cada etapa.",
  "bottomHeadline": "Defina o próximo passo para sua equipe",
  "bottomIntro": "Agende uma conversa gratuita de 15 minutos por telefone para definir por onde começar, identificar o principal obstáculo à adoção e solicitar uma cotação de parceiro para o plano OpenAI adequado à sua equipe.",
  "bottomCta": "Agendar uma conversa sobre IA",
  "disclaimer": "OpenAI e o logotipo da OpenAI são marcas da OpenAI. A Tech Leaders participa do programa OpenAI SMB Channel Partner. As condições comerciais de parceiro estão disponíveis para pequenas e médias empresas que atendam aos critérios de elegibilidade, em planos empresariais OpenAI elegíveis, e serão confirmadas na sua cotação.",
  "keywords": [
    "parceiro OpenAI",
    "OpenAI para empresas",
    "ChatGPT para empresas",
    "condições comerciais OpenAI",
    "adoção de IA",
    "capacitação em IA",
    "IA para pequenas e médias empresas"
  ],
  "pricingSteps": [
    "Conte quantas pessoas precisam das ferramentas de IA e o que elas precisam fazer com elas.",
    "Confirmamos a elegibilidade da sua empresa e enviamos uma cotação de parceiro para o plano OpenAI adequado.",
    "Suas licenças são ativadas e o plano de adoção começa na mesma semana."
  ],
  "supportPoints": [
    "Condições comerciais de parceiro em planos empresariais da OpenAI",
    "Alinhamento da liderança sobre os casos de uso de IA prioritários",
    "Capacitação prática adaptada ao trabalho da sua equipe",
    "Desenho de processos e orientação para implementação",
    "Acompanhamento contínuo da implementação e adoção",
    "Uso responsável com governança adequada"
  ],
  adoptionSteps: [
    { ...adoptionSteps[0], title: "Avaliar", description: "Identifique oportunidades úteis de IA, escolha por onde começar e conecte as prioridades de adoção ao trabalho real da empresa." },
    { ...adoptionSteps[1], title: "Capacitar", description: "Ofereça capacitação prática para que líderes e equipes entendam as ferramentas, usem a IA com responsabilidade e ganhem confiança juntos." },
    { ...adoptionSteps[2], title: "Adotar", description: "Transforme casos de uso promissores em processos de trabalho consistentes, com orientação para a implementação e acompanhamento contínuo." },
  ],
  pricingBenefits: [
    { ...pricingBenefits[0], title: "Condições comerciais de parceiro", description: "Como participante do programa OpenAI SMB Channel Partner, podemos oferecer a pequenas e médias empresas elegíveis preços reduzidos em planos empresariais da OpenAI, abaixo dos valores da contratação direta." },
    { ...pricingBenefits[1], title: "Licenças e adoção com um só parceiro", description: "Contrate suas licenças pela Tech Leaders junto com capacitação, desenho de processos e apoio à implementação, simplificando a coordenação entre fornecedor e consultoria." },
    { ...pricingBenefits[2], title: "Contratação simplificada", description: "Uma conversa, uma cotação e uma fatura. Cuidamos dos trâmites da parceria para que sua equipe possa começar sem um processo de compra demorado." },
  ],
};

export const openaiPartnerContent: Record<PageLocale, typeof en> = { en, "pt-BR": pt };
